/**
 * The URL is the app's state: the view is the path (`/`, `/board`, `/activity`), and the open issue,
 * the create form and the current project are search parameters (`?issue=…`, `?new`,
 * `?project=…`). So every view and selection is a link someone else can open — onto the same live
 * data, in the same project.
 *
 * The project is here rather than in component state for the reason the rest of it is: it decides
 * what every list and board column reads, so a link that does not carry it is a link to a
 * different page. `null` means "whichever one the switcher lands on first", which the app resolves
 * once the project list arrives.
 */
import { useSyncExternalStore } from 'react';

export type View = 'list' | 'board' | 'activity';
export interface Route { view: View; issue: string | null; creating: boolean; project: string | null }

const PATHS: Record<View, string> = { list: '/', board: '/board', activity: '/activity' };

function parse(): Route {
  const { pathname, search } = window.location;
  const params = new URLSearchParams(search);
  const view: View = pathname.startsWith('/board') ? 'board' : pathname.startsWith('/activity') ? 'activity' : 'list';
  return { view, issue: params.get('issue'), creating: params.has('new'), project: params.get('project') };
}

let route = parse();
const watchers = new Set<() => void>();
const changed = () => { route = parse(); for (const w of watchers) w(); };
window.addEventListener('popstate', changed);

export function useRoute(): Route {
  return useSyncExternalStore(cb => { watchers.add(cb); return () => watchers.delete(cb); }, () => route);
}

/** Move to a view and/or open something, keeping whatever the patch does not mention. */
export function navigate(patch: Partial<Route>) {
  const next = { ...route, ...patch };
  const params: string[] = [];
  if (next.project) params.push(`project=${encodeURIComponent(next.project)}`);
  if (next.issue) params.push(`issue=${encodeURIComponent(next.issue)}`);
  if (next.creating) params.push('new');
  const url = PATHS[next.view] + (params.length ? `?${params.join('&')}` : '');
  if (url !== window.location.pathname + window.location.search) window.history.pushState(null, '', url);
  changed();
}
